import styled, { css } from 'styled-components';
import { always, cond, ifElse, propEq } from 'ramda';
import Markdown from 'react-markdown';
import { ReactComponent as ArrowButtonSVG } from '../../images/arrow-button.svg';
import { ELEMENTS_TYPES, WIDTH_TYPES } from '../../utils/constants';
import { RED_COLORS_SET } from '../../utils/colors';
import { media } from '../../utils/media';
import { ContentContainer } from '../layout/layout.styles';
import { CARD_MARGIN, CARD_MARGIN_MOBILE } from './videoCardCarousel.constants';
import { Slider as SliderComponent, SLIDER_SELECTORS } from '../slider';

export const Title = styled(Markdown)`
  h3{
    font-size: 18px;
    font-weight: 600;
    line-height: 1.33em;
    letter-spacing: 0.45px;
    flex: 1 auto;
    margin: 0 20px 0 0;
    text-transform: uppercase;
  }
`;

export const Navigation = styled.div`
  flex: 0 auto;

  @media print {
    display: none;
  }
`;

export const Button = styled.button`
  border: none;
  background: none;
  margin: 0 0 0 10px;
  padding: 0;
  border-radius: 0;
  outline: none;
  cursor: pointer;

  path {
    stroke-width: 2.667;
  }
`;

export const PreviousArrowIcon = styled(ArrowButtonSVG)``;

export const NextArrowIcon = styled(ArrowButtonSVG)`
  transform: rotate(180deg);
`;

export const Header = styled(ContentContainer)`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 33px;
  padding: 0 20px 0 50px;
  justify-content: space-between;
  ${media.tablet`
    width: 100%;
  `}

  ${media.desktop`
    padding: 0 80px 0 0;
    margin: 0 0 33px 0;
    width: 100%;
  `};
`;

export const VideoCard = styled.div`
  padding: 10px 20px;
  transition: transform 0.3s ease-in;
  ${media.desktop`
    width: unset;
    height: unset;
  `};
`

export const ImageComponent = styled.img`
  height: 60px;
  width: auto;
`

export const CardTitle = styled.div`
  font-family: 'IBM Plex Sans';
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding-left: 19px;
  color: ${({ theme }) => theme.color};
`

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 31px;
  margin: 0 30px;
  ${media.desktop`
    margin: 0 45px;
  `}
`

export const CardContent = styled(Markdown)`
  h3 {
    font-family: 'IBM Plex Sans';
    font-style: normal;
    font-weight: 600;
    font-size: 12px;
    line-height: 14px;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    margin: 0 30px;
    color: ${({ theme }) => theme.color};
  }
  p { 
    font-family: 'IBM Plex Sans';
    font-style: normal;
    font-weight: 300;
    font-size: 14px;
    line-height: 18px;
    margin: 0 30px 15px 30px;
  }
  ${media.desktop`
    h3 {
      margin: 0 45px;
    }
    p {
      margin: 0 45px 15px;
    }
  `}
`;

export const Slider = styled(SliderComponent)`
  ${SLIDER_SELECTORS.FIRST_ITEM} {
    margin-left: 11px;

    ${media.desktop`
      margin-left: 0;
    `}
  }

  @media print {
    display: none;
  }
`;

export const Card = styled.div`
  padding: 10px 20px;
  transition: transform 0.3s ease-in;
  ${media.desktop`
    width: unset;
    height: unset;
  `};
`;

const colorsStyles = ifElse(
  propEq('useCustomTheme', true),
  always(css`
    ${Card} {
      background-color: ${RED_COLORS_SET.background};
    }

    ${Title}, ${CardContent}[type="${ELEMENTS_TYPES.PLAIN_TEXT}"] {
      color: ${RED_COLORS_SET.color};
    }

    ${Button} circle {
      fill: ${RED_COLORS_SET.background};
    }

    ${Button} path {
      stroke: ${RED_COLORS_SET.color};
    }
  `),
  ({ theme }) => css`
    ${Card} {
      background-color: ${theme.background};
    }

    ${Title} {
      color: ${theme.color};
    }

    ${Button} circle {
      fill: ${theme.background};
    }

    ${Button} path {
      stroke: ${theme.color};
    }
  `
);

const getContainerWidth = (width) => `calc(${width}px + (100% - ${width}px) / 2)`;

const setCardStyles = (whenTrue, whenFalse) => css`
  ${Card} {
    ${ifElse(propEq('cardType', WIDTH_TYPES.SHORT), always(whenTrue), always(whenFalse))}
  }
`;

export const Container = styled(ContentContainer)`
  ${colorsStyles};
  display: flex;
  flex-direction: column;
  margin-bottom: 50px;
  margin-right: 0;
  width: 100vw;

  ${SLIDER_SELECTORS.ITEM_OUTER_WRAPPER} {
    padding: 0 ${CARD_MARGIN_MOBILE / 2}px;
  }

  ${setCardStyles(
    css`
      padding: 0 0 21px;
    `,
    css`
      padding: 0 0 21px;
    `
  )}

  ${media.tablet`
    width: ${getContainerWidth(640)};

    ${setCardStyles(
      css`
        min-width: 300px;
      `,
      css`
        min-width: 300px;
      `
    )}
  `};

  ${media.desktop`
    width: ${getContainerWidth(800)};

    ${setCardStyles(
      css`
        min-width: 240px;
      `,
      css`
        min-width: 380px;
      `
    )}

    .slick-slide {
      padding: 0 ${CARD_MARGIN / 2}px;

      &:first-child {
        padding-left: unset;
      }
    }
  `};

  ${media.desktopWide`;
    width: ${getContainerWidth(1000)};

    ${setCardStyles(
      css`
        min-width: 306px;
      `,
      css`
        min-width: 480px;
      `
    )}
  `};
`;

export const CardContentWrapper = styled.div``;

export const CardWrapper = styled.div`
  display: none;
  flex-wrap: wrap;
  padding-right: 50px;

  ${Card} {
    width: calc(50% - 10px);
    margin-bottom: 20px;

    &:nth-child(2n) {
      margin-left: 20px;
    }
  }

  @media print {
    display: flex;
  }
`;